class CapabilitiesService {
    constructor() {
        // if adaptive pixelation is on, we want this framerate when presenting in vr mode
        this.targetFpsHmd = 95;
        this.displayVrViewOnCanvas = false;
        this.platform = "userAgentData" in navigator ? navigator.userAgentData.platform : navigator.userAgent || "unknown";
        this.isOculusBrowser = navigator.userAgent.includes("Oculus") ||
            navigator.userAgent.includes("OculusBrowser") ||
            navigator.userAgent.includes("Quest") ||
            navigator.userAgent.includes("MetaBrowser");
        this.isFirefoxReality = /(Mobile VR)/i.test(window.navigator.userAgent);
        this.isTablet = /ipad|Nexus (7|9)|xoom|sch-i800|playbook|tablet|kindle/i.test(window.navigator.userAgent);
        this.isIOS = /iPad|iPhone|iPod/.test(this.platform);
        this.isR7 = window.navigator.userAgent.includes("R7 Build");
        this.isMobileVR = this.isOculusBrowser || this.isFirefoxReality;
        this.browser = window.navigator.userAgent;
        this.stereoView = window.location.href.includes("stereo");
        if (window.location.href.includes("displayVrViewOnCanvas")) {
            this.displayVrViewOnCanvas = true;
        }
        this.isLowEndGPU = this.detectLowEndGPU();
        this.isHighEndGPU = !this.isLowEndGPU && !this.isMobileVR && this.detectHighEndGPU();
        // Mobile-VR-Geräte (Meta Quest u. ä.) brauchen antialias = true, damit der
        // Three.js WebXRManager den XR-Layer mit MSAA (4 Samples) erstellt.
        // Ohne dieses Flag rendert Three.js den XR-Layer mit 0 Samples → starkes Aliasing in VR.
        // Der Renderer wird von Three.js bei Sitzungsstart einmalig für antialias abgefragt
        // (WebXRManager.js, Zeile 40 / 346: `samples: attributes.antialias ? 4 : 0`).
        this.antialias = !this.isLowEndGPU || this.isMobileVR;
        this.shadow = !this.isLowEndGPU;
    }
    /**
     * Erkennt Low-End-GPUs anhand der WebGL-Renderer-Zeichenkette sowie des Gerätespeichers.
     * Bekannte integrierte / schwache GPUs (Intel HD, Mali-3/4xx, Adreno 3/4xx …)
     * werden als "schwach" eingestuft.
     *
     * Meta Quest-Geräte (Oculus-Browser / Firefox Reality) werden ebenfalls als Low-End
     * eingestuft: Sie laufen auf mobilen Snapdragon-Chips (Adreno 540–740) mit eigenem
     * Thermal-Budget, das deutlich unter dem eines Desktop-PCs liegt.
     */
    detectLowEndGPU() {
        var _a;
        // Meta Quest / Oculus Browser / Firefox Reality → immer als Low-End behandeln
        if (this.isMobileVR) {
            console.info("[capabilities] Mobile-VR-Browser erkannt (Meta Quest / Firefox Reality). Grafikqualität wird reduziert.");
            return true;
        }
        try {
            const canvas = document.createElement("canvas");
            const gl = (_a = canvas.getContext("webgl2")) !== null && _a !== void 0 ? _a : canvas.getContext("webgl");
            if (gl) {
                const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
                if (debugInfo) {
                    const gpuRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
                    // Integrierte Intel-Grafik, ältere Adreno/Mali-Chips und PowerVR.
                    // Adreno [2-7]xx deckt ab:
                    //   2xx–4xx  → alte Android-Mittelklasse
                    //   5xx      → Quest 1 (Adreno 540), Snapdragon 835
                    //   6xx      → Quest 2 (Adreno 650), Quest Pro (Adreno 660)
                    //   7xx      → Quest 3 (Adreno 740)
                    // Renderer-Strings enthalten häufig "(R)" oder "®" zwischen Hersteller und Modell,
                    // z. B. "Intel(R) UHD Graphics 620" oder "Adreno (TM) 650".
                    if (/intel(\(r\)|®)?\s+(hd|uhd)|intel(\(r\)|®)?\s+iris(?! pro| plus| xe)|mali-[34]\d\d|adreno\s*(\(tm\))?\s*[2-7]\d\d|sgx\d|powervr/i.test(gpuRenderer)) {
                        console.info(`[capabilities] Low-End-GPU erkannt: "${gpuRenderer}". Grafikqualität wird reduziert.`);
                        return true;
                    }
                }
            }
        }
        catch (_) {
            // Ignore – im Zweifelsfall nicht als Low-End einschätzen
        }
        // Fallback: Wenig Arbeitsspeicher (< 4 GB) ist ein guter Indikator für schwache Hardware
        if (typeof navigator.deviceMemory === "number" && navigator.deviceMemory < 4) {
            console.info(`[capabilities] Wenig Gerätespeicher (${navigator.deviceMemory} GB). Grafikqualität wird reduziert.`);
            return true;
        }
        return false;
    }
    /**
     * Erkennt dedizierte High-End-GPUs anhand der WebGL-Renderer-Zeichenkette.
     * Erkannte Familien:
     *   NVIDIA GeForce RTX (alle Generationen, inkl. Laptop-Varianten)
     *   AMD Radeon RX 6000- und 7000-Serie
     *   Intel Arc A-Serie
     */
    detectHighEndGPU() {
        var _a;
        try {
            const canvas = document.createElement("canvas");
            const gl = (_a = canvas.getContext("webgl2")) !== null && _a !== void 0 ? _a : canvas.getContext("webgl");
            if (gl !== null) {
                const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
                if (debugInfo !== null) {
                    const gpuRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
                    // NVIDIA RTX (alle Typen inkl. "laptop gpu"), AMD RX 6xxx / 7xxx, Intel Arc A-Serie
                    if (/geforce\s+rtx|radeon\s+rx\s+[67]\d{3}|intel\s+arc\s+a/i.test(gpuRenderer)) {
                        const rendererName = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
                        console.info(`[capabilities] High-End-GPU erkannt: "${rendererName}". Maximale VR-Qualität wird aktiviert.`);
                        return true;
                    }
                }
            }
        }
        catch (_) {
            // Ignore
        }
        return false;
    }
    isMobile() {
        let isMobile = false;
        if ("userAgentData" in navigator) {
            isMobile = navigator.userAgentData.mobile;
        }
        const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
        if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(userAgent) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(userAgent.substring(0, 4))) {
            isMobile = true;
        }
        if (this.isIOS || this.isTablet || this.isR7) {
            isMobile = true;
        }
        if (this.isMobileVR) {
            isMobile = false;
        }
        return isMobile;
    }
}
export const capabilitiesService = new CapabilitiesService();
